/**
 * The signup password rules, as a list the UI can show while someone types.
 *
 * MIRRORS backend/utils/passwordValidator.js. That file is the authority — it is
 * what actually accepts or rejects — and every rule there is represented here.
 * A checklist that covers only SOME of the server's rules is worse than none: it
 * tells you that you are done, and then the server disagrees.
 *
 * The rule people actually trip on is the character set. The server regex is
 *
 *     /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
 *
 * and that character class is a WHITELIST. `Passw0rd#` fails — not for want of a
 * special character, but because `#` is not one of the eight permitted. The
 * server's message says "must include ... one special character (@$!%*?&)",
 * which reads as "you forgot one" to someone who plainly did not. So the
 * checklist states the allowed set up front and names the offending character
 * when one appears.
 *
 * SHOWN vs ENFORCED are deliberately different sets. Every rule here decides
 * whether the button is enabled; only `display: true` ones appear as ticks. The
 * two "did you pick something guessable" rules are real server rules, but a
 * permanent green tick saying "not a commonly used password" is noise on a list
 * someone reads while typing. They surface as a message only when they fail —
 * because a disabled button with nothing on screen explaining it is the exact
 * complaint this whole change exists to fix.
 */

/** The only special characters the backend regex permits. */
export const ALLOWED_SPECIALS = '@$!%*?&';

/** Everything the backend's character class allows, as a test. */
const ALLOWED_CHAR = /[A-Za-z\d@$!%*?&]/;

/** Rendered with spaces so `@$!%*?&` is readable rather than a smear. */
const SPECIALS_DISPLAY = ALLOWED_SPECIALS.split('').join(' ');

// Kept in step with commonPasswords in backend/utils/passwordValidator.js.
const COMMON = [
  'password', 'admin', '123456', 'qwerty', 'welcome', 'letmein',
  'football', 'iloveyou', 'monkey', 'password1', 'abc123',
];

/**
 * Characters the user typed that the backend will refuse, de-duplicated and in
 * the order they appear — so the hint can name them instead of saying "invalid
 * character" and leaving a hunt.
 */
export function disallowedCharsIn(password) {
  const bad = [];
  for (const ch of String(password || '')) {
    if (!ALLOWED_CHAR.test(ch) && !bad.includes(ch)) bad.push(ch);
  }
  return bad;
}

export const PASSWORD_RULES = [
  {
    id: 'length',
    display: true,
    label: 'At least 8 characters',
    test: (p) => String(p || '').length >= 8,
  },
  {
    id: 'lowercase',
    display: true,
    label: 'One lowercase letter',
    test: (p) => /[a-z]/.test(p || ''),
  },
  {
    id: 'uppercase',
    display: true,
    label: 'One uppercase letter',
    test: (p) => /[A-Z]/.test(p || ''),
  },
  {
    id: 'number',
    display: true,
    label: 'One number',
    test: (p) => /\d/.test(p || ''),
  },
  {
    // Two server rules on one line, because to a reader they are one idea:
    // which symbols may I use. Splitting them produced a list where "one
    // special character" sat ticked green next to a separate red line about
    // symbols — technically accurate, and baffling.
    id: 'special',
    display: true,
    label: `One special character: only ${SPECIALS_DISPLAY} are allowed`,
    test: (p) => /[@$!%*?&]/.test(p || '') && disallowedCharsIn(p).length === 0,
    hint: (p) => {
      const bad = disallowedCharsIn(p);
      if (!bad.length) return null;
      return bad.length === 1
        ? `“${bad[0]}” is not allowed`
        : `${bad.map((c) => `“${c}”`).join(', ')} are not allowed`;
    },
  },
  {
    id: 'notCommon',
    display: false,
    label: 'Not a commonly used password',
    message: 'That password is too common: pick something less guessable.',
    test: (p) => !COMMON.includes(String(p || '').toLowerCase()),
  },
  {
    id: 'notPersonal',
    display: false,
    label: 'Does not contain your name or email',
    message: 'Your password can’t contain your name or email address.',
    // Mirrors checkCommonPatterns: only pieces longer than 3 characters count,
    // so a user named "Jo" is not told their password contains their name.
    test: (p, ctx = {}) => {
      const lower = String(p || '').toLowerCase();
      if (!lower) return true;
      const pieces = [ctx.name, String(ctx.email || '').split('@')[0]]
        .filter((x) => x && String(x).length > 3);
      return !pieces.some((piece) => lower.includes(String(piece).toLowerCase()));
    },
  },
];

/**
 * @returns {{
 *   valid: boolean,              // every rule, shown or not
 *   results: Array<{id,label,ok,hint}>,  // the ticked list, always in full
 *   blockers: string[],          // messages for failing rules that are NOT shown
 * }}
 *
 * `results` is always the complete display list, so the UI can render every rule
 * and mark each — a list that removes items as they pass reflows under the
 * cursor exactly while it is being read.
 */
export function evaluatePassword(password, ctx = {}) {
  const evaluated = PASSWORD_RULES.map((rule) => ({
    rule,
    ok: rule.test(password, ctx),
  }));

  return {
    valid: evaluated.every((e) => e.ok),
    results: evaluated
      .filter((e) => e.rule.display)
      .map(({ rule, ok }) => ({
        id: rule.id,
        label: rule.label,
        ok,
        hint: !ok && rule.hint ? rule.hint(password) : null,
      })),
    // Only once something has been typed: an untouched field should not accuse
    // anyone of choosing a common password.
    blockers: String(password || '').length
      ? evaluated.filter((e) => !e.ok && !e.rule.display && e.rule.message)
        .map((e) => e.rule.message)
      : [],
  };
}
